import {
  Alert,
  Button,
  Grid,
  Icon,
  RadioButton,
  RadioGroup,
  Typography,
} from '@statsbomb/kitbag-components';
import {
  Playlist,
  VideoEvent,
  _IgnoreAlignmentCheckProvider,
} from '@statsbomb/video-client-js';
import { useAtom, useAtomValue } from 'jotai';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import useSWRRevalidateByIncludes from '../../../hooks/useSWRRevalidateByIncludes';
import {
  videoReferenceSelectedForAlignmentCheckAtom,
  videoSelectedForAlignmentCheckAtom,
} from '../../../atoms/videoAlignmentRequired';
import {
  alignmentNotesAtom,
  playbackAndMinutesOkAtom,
  draftIsAlignedAtom,
} from '../../../atoms/videoAlignmentCheck';
import useApi, { API_ENDPOINTS } from '../../../hooks/api';
import RenderGate from '../../RenderGate';
import {
  ButtonContainer,
  Container,
  GridContainer,
  ModalContent,
  StatusContainer,
  TextArea,
  VideoContainer,
} from './steps.styles';
import { VideoProvider } from '../../VideoProvider/VideoProvider';

export function ReviewEventStep() {
  const { get, isFetching, errorMessage } = useApi();
  const intl = useIntl();
  const videoReferenceSelectedForAlignmentCheck = useAtomValue(
    videoReferenceSelectedForAlignmentCheckAtom,
  );
  const [isAligned, setIsEventAligned] = useAtom(draftIsAlignedAtom);
  const videoSelectedForAlignmentCheck = useAtomValue(
    videoSelectedForAlignmentCheckAtom,
  );
  const [playbackAndMinutesOk, setPlaybackAndMinutesOk] = useAtom(
    playbackAndMinutesOkAtom,
  );
  const [alignmentNotes, setAlignmentNotes] = useAtom(alignmentNotesAtom);
  const [lastShotEvent, setLastShotEvent] = useState(null);

  useSWRRevalidateByIncludes(lastShotEvent);

  useEffect(() => {
    if (!isAligned)
      setIsEventAligned(videoSelectedForAlignmentCheck?.videoInfo?.isAligned);
  }, [videoSelectedForAlignmentCheck?.videoInfo?.isAligned]);

  useEffect(() => {
    const getLastShotEvent = async () => {
      const response = await get(
        // eslint-disable-next-line max-len
        `${API_ENDPOINTS.videoService}/streams/${videoReferenceSelectedForAlignmentCheck.sport}/${videoReferenceSelectedForAlignmentCheck.matchId}/${videoReferenceSelectedForAlignmentCheck.period}/last_shot_event`,
      );

      setLastShotEvent(response?.data?.last_shot_event);
    };

    getLastShotEvent();
  }, []);

  /**
   * This nested VideoProvider is placed here intentionally to limit the scope of its state.
   *
   * Inside the VideoProvider, Jotai atoms manage the state of playlist items. When a playlist
   * item is first requested, it’s stored in an atom to prevent duplicate fetches for the same item.
   *
   * In this alignment flow, however, the user may change the underlying video for the event.
   * To ensure the event data is always up to date, we want it to be re-fetched on every render
   * of this component.
   *
   * By nesting the VideoProvider here, we keep its state scoped to this component and guarantee
   * that, in combination with the useSWRRevalidateByIncludes hook above, the event is re-fetched each time it renders.
   */
  return (
    <VideoProvider excludeKeyboardBindings streamsOnly>
      <ModalContent>
        <Grid>
          <Grid item xs={12} lg={6}>
            <RenderGate
              isFetching={isFetching}
              toastBlocksRender={false}
              errorMessage={errorMessage}
            >
              <VideoContainer>
                {/* This provider will ensure that the event requested will not prefer aligned videos which is a
              requirement here */}
                {/* eslint-disable-next-line react/jsx-pascal-case */}
                <_IgnoreAlignmentCheckProvider>
                  <VideoEvent autoPlay event={lastShotEvent} />
                </_IgnoreAlignmentCheckProvider>
              </VideoContainer>
            </RenderGate>
          </Grid>
          <Grid item xs={12} lg={6}>
            <GridContainer>
              <Container>
                <RenderGate
                  isFetching={isFetching}
                  toastBlocksRender={false}
                  errorMessage={errorMessage}
                >
                  <Playlist />
                </RenderGate>
                <RadioGroup
                  alignment="horizontal"
                  groupId="event-alignment-radio-group"
                  groupTitle={intl.formatMessage({
                    defaultMessage: 'Is the event aligned with the video?',
                    id: 'e32f4',
                  })}
                  size="small"
                >
                  <RadioButton
                    checked={isAligned}
                    id="event-alignment-radio-button-yes"
                    label={intl.formatMessage({
                      defaultMessage: 'Yes',
                      id: '93cba',
                    })}
                    onChange={() => setIsEventAligned(true)}
                    size="small"
                    value="1"
                  />
                  <RadioButton
                    checked={isAligned === false}
                    id="event-alignment-radio-button-no"
                    label={intl.formatMessage({
                      defaultMessage: 'No',
                      id: 'bafd7',
                    })}
                    onChange={() => setIsEventAligned(false)}
                    size="small"
                    value="2"
                  />
                </RadioGroup>
              </Container>
              <Container>
                {isAligned ? (
                  <Alert type="success">
                    {intl.formatMessage({
                      defaultMessage:
                        'Video will be made available in Hudl Statsbomb.',
                      id: '91577',
                    })}
                  </Alert>
                ) : (
                  <>
                    <div>
                      <Typography variant="headingMedium">
                        {intl.formatMessage({
                          defaultMessage: 'Review Video Issue',
                          id: '01412',
                        })}
                      </Typography>
                      <Typography variant="bodySmall" as="div">
                        <StatusContainer>
                          <Icon variant="Info" size="small" />
                          {intl.formatMessage({
                            defaultMessage: `Video will be unavailable in Hudl Statsbomb until the alignment issue
                    has been resolved. Review video source to identify the issue.`,
                            id: 'b7ee4',
                          })}
                        </StatusContainer>
                      </Typography>
                    </div>
                    <ButtonContainer>
                      <Button
                        size="small"
                        variant="secondary"
                        target="_blank"
                        as="a"
                        href={`/download/football/${videoReferenceSelectedForAlignmentCheck.matchId}`}
                      >
                        <Icon variant="ForwardArrow" size="small" />
                        {intl.formatMessage({
                          defaultMessage: 'Review Video Source',
                          id: 'a4974',
                        })}
                      </Button>
                    </ButtonContainer>
                    <RadioGroup
                      alignment="horizontal"
                      groupId="video-playback-radio-group"
                      groupTitle={intl.formatMessage({
                        defaultMessage:
                          'Does the licensed video playback correctly and include all match minutes?',
                        id: '842e9',
                      })}
                      size="small"
                    >
                      <RadioButton
                        checked={playbackAndMinutesOk}
                        id="video-playback-radio-button-yes"
                        label={intl.formatMessage({
                          defaultMessage: 'Yes',
                          id: '93cba',
                        })}
                        onChange={() => setPlaybackAndMinutesOk(true)}
                        size="small"
                        value="1"
                      />
                      <RadioButton
                        checked={playbackAndMinutesOk === false}
                        id="video-playback-radio-button-no"
                        label={intl.formatMessage({
                          defaultMessage: 'No',
                          id: 'bafd7',
                        })}
                        onChange={() => setPlaybackAndMinutesOk(false)}
                        size="small"
                        value="2"
                      />
                    </RadioGroup>
                    <Typography variant="headingMedium">
                      <label htmlFor="alignment-notes-text-area">
                        {intl.formatMessage({
                          defaultMessage: 'Notes (optional)',
                          id: 'f7ee7',
                        })}
                      </label>
                    </Typography>
                    <Typography variant="bodyMedium" as="div">
                      <TextArea
                        id="alignment-notes-text-area"
                        value={alignmentNotes}
                        onChange={(e) => setAlignmentNotes(e.target.value)}
                        rows={4}
                        maxLength={1000}
                        placeholder={intl.formatMessage({
                          defaultMessage: 'Start typing...',
                          id: 'ad666',
                        })}
                      />
                    </Typography>
                  </>
                )}
              </Container>
            </GridContainer>
          </Grid>
        </Grid>
      </ModalContent>
    </VideoProvider>
  );
}

export default ReviewEventStep;
